import React, { useRef, useState, useEffect, useCallback } from "react";
import { useBottomScroll } from "../../hooks/useBottomScrollContext";
import {
  AnnotationShape,
  AnnotationViewer,
  drawLayer,
  Stage,
  PointerPosition,
  getImagesFromPDF,
} from "react-mindee-js";
import Lens from "./Lens";
import { MdPrint } from "react-icons/md";
import { FaLink, FaUnlink } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

export default function BasicExampleWithImage({
  orderImage,
  orderPdf,
  shapes,
  hoveredShapeId,
  onHoveredShapeDraw,
  urlPdf,
  orderId,
  onScrollSyncChange,
}) {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosPrivate();

  const [imageLoaded, setImageLoaded] = useState(false);
  const [isLensVisible, setIsLensVisible] = useState(false);
  const [isZoneVisible, setIsZoneVisible] = useState(true);
  const [isScrollSynced, setIsScrollSynced] = useState(true);
  const [localScrollOffset, setLocalScrollOffset] = useState(0);
  const [frozenAtBottom, setFrozenAtBottom] = useState<number | null>(null);
  const isPdf = orderPdf?.source;
  const [annotationData, setAnnotationData] = useState<{
    image: string;
    shapes: AnnotationShape[];
  } | null>(null);
  
  useEffect(() => {
    if (isPdf && shapes != null) {
      fetch(orderPdf.source)
        .then(response => response.blob())
        .then(pdfBlob => {
          const pdfUrl = URL.createObjectURL(pdfBlob);
          getImagesFromPDF(pdfUrl)
            .then((pdfImages: string[]) => {
              if (shapes) {
                setAnnotationData({ image: pdfImages[0], shapes: [...shapes] });
                setImageLoaded(true);
              }
            })
            .catch(error => {
              console.error("Failed to fetch PDF images:", error);
            });
        })
        .catch(error => {
          console.error("Failed to fetch PDF:", error);
        });
    } else if (!isPdf && orderImage?.source && shapes != null) {
      setAnnotationData({ image: orderImage.source, shapes: [...shapes] });
      setImageLoaded(true);
    } else if (orderImage?.source && shapes === undefined) {
      setAnnotationData({ image: orderImage.source, shapes: [] });
      setImageLoaded(true);
    } else {
      setImageLoaded(false); 
    }
  }, [orderPdf, orderImage, isPdf, shapes]);

  const [pointerPosition, setPointerPosition] = useState<PointerPosition>();
  const annotationViewerStageRef = useRef<null | Stage>(null);
  const { atBottom } = useBottomScroll();

  const [viewerHeight, setViewerHeight] = useState<string | null>(null);
  

  const containerRef = useRef<HTMLDivElement>(null);

useEffect(() => {
  const scrollElement = document.querySelector('#scrollElement');
  const container = containerRef.current;
  if (!scrollElement || !container) return;

  const handleWheel = (event: WheelEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isScrollSynced) {
      scrollElement.scrollBy({
        top: event.deltaY
      });
    } else {
      // Limiter le scroll pour garder l'image visible
      const minOffset = -(window.innerHeight - 200);
      const maxOffset = window.innerHeight - 200;
      setLocalScrollOffset((prev) => Math.min(maxOffset, Math.max(minOffset, prev + event.deltaY)));
    }
  };

  container.addEventListener("wheel", handleWheel, {
    passive: false,
    capture: true,
  });

  return () => {
    container.removeEventListener("wheel", handleWheel, { capture: true });
  };
}, [isScrollSynced]);

  useEffect(() => {
    if (orderImage) {
      setAnnotationData({ image: orderImage.source, shapes });
      // Reset le scroll local et repasser en mode synchronisé lors du changement de page
      setLocalScrollOffset(0);
      setFrozenAtBottom(null);
      setIsScrollSynced(true);
      onScrollSyncChange?.(true);
    }
  }, [orderImage, shapes]);

  const [currentHoveredShapeId, setCurrentHoveredShapeId] = useState(null); // Status to track the ID of the shape currently hovered over

  const onShapeMouseEnter = useCallback(
    (shape: AnnotationShape) => {
      // Check to be sure that the shape hovered over is different from the last shape hovered over.
      if (currentHoveredShapeId !== shape.id) {
        setCurrentHoveredShapeId(shape.id); // Mettez à jour l'état avec la nouvelle forme survolée
        onHoveredShapeDraw("mouseEnter", shape.id); // Envoyer les informations au parent
      }
    },
    [currentHoveredShapeId, onHoveredShapeDraw],
  );

  const onShapeMouseLeave = useCallback(
    (shape: AnnotationShape) => {
      // Reset state when leaving a form
      setCurrentHoveredShapeId(null);
      onHoveredShapeDraw("mouseLeave", shape.id); // Envoyer les informations au parent
      drawLayer(annotationViewerStageRef.current!); // Redessiner la couche pour appliquer la configuration
    },
    [onHoveredShapeDraw],
  );

  // Function to apply the hover style directly to the canvas when the product div is hovered over
  const applyHoverStyle = useCallback((shapeId, style) => {
    const stage = annotationViewerStageRef.current;
    if (!stage) return;

    const shape = stage.findOne(`#${shapeId}`);
    if (shape) {
      shape.setAttrs(style);
      stage.batchDraw(); // Uses batchDraw for better performance
    }
  }, []);

  // Effect to manage hovering based on hoveredShapeId
  const [highlightedCanvasId, setHighlightedCanvasId] = useState(null);
  useEffect(() => {
    if (hoveredShapeId && hoveredShapeId.action === "mouseEnter") {
      if (highlightedCanvasId !== null) {
        applyHoverStyle(highlightedCanvasId, {
          fill: "transparent",
          opacity: "1",
        });
        setHighlightedCanvasId(null);
      }
      applyHoverStyle(hoveredShapeId.canvasId, {
        fill: "orange",
        opacity: "0.3",
      });
      setHighlightedCanvasId(hoveredShapeId.canvasId);
    } else if (
      hoveredShapeId &&
      hoveredShapeId.action === "mouseLeave" &&
      hoveredShapeId.canvasId === highlightedCanvasId
    ) {
      applyHoverStyle(hoveredShapeId.canvasId, {
        fill: "transparent",
        opacity: "1",
      });
      setHighlightedCanvasId(null);
    }
  }, [hoveredShapeId, applyHoverStyle, highlightedCanvasId]);

  useEffect(() => {
    if (orderImage) {
      if (orderImage.ratio == null) {
        setViewerHeight("100%");
      } else {
        const ratio = orderImage.ratio;
        const updateSize = () => {
          const container = document.querySelector(".w-full.h-full");
          if (container) {
            const containerWidth = container.clientWidth;
            if (containerWidth) {
              const height = containerWidth / ratio;
              setViewerHeight(`${height}px`);
            }
          }
        };

        window.addEventListener("resize", updateSize);
        updateSize(); // Initialize immediately

        return () => window.removeEventListener("resize", updateSize);
      }
    }
  }, [orderImage]);

  const [customZoomLevel, setCustomZoomLevel] = useState(1);
  const changeScale = (modifier: number) => {
    const oldZoomScale = annotationViewerStageRef.current!.getAttr("zoomScale");
    const newScale = modifier * oldZoomScale;
    setCustomZoomLevel(newScale);
  };

  const updatePointerPosition = (pointerPosition: PointerPosition) => {
    setPointerPosition(pointerPosition);
  };

  const viewerHeightNumber = viewerHeight
    ? parseInt(viewerHeight.replace("px", ""), 10)
    : 0;

  // En mode désync, utiliser la valeur figée de atBottom au moment du switch
  const effectiveAtBottom = isScrollSynced ? atBottom.bottom : (frozenAtBottom ?? atBottom.bottom);

  const atComputedTopValue = isLensVisible
    ? (effectiveAtBottom * viewerHeightNumber) / 100 - 200
    : (effectiveAtBottom * viewerHeightNumber) / 100 - 50;

  let atComputedTop: string;
  if (isScrollSynced) {
    atComputedTop = `calc(${effectiveAtBottom}% - ${atComputedTopValue}px)`;
  } else {
    // En mode désynchronisé : position figée + offset local (scroll gauche uniquement)
    atComputedTop = `calc(${effectiveAtBottom}% - ${atComputedTopValue}px - ${localScrollOffset}px)`;
  }

  const imprimerPDF = async () => {
    if (!orderId) {
      return;
    }

    try {
      await axiosPrivate.post(`/orders/${orderId}/print`);
      if (urlPdf && urlPdf.length > 0) {
        const fenetre = window.open(urlPdf[0], "_blank");
        if (fenetre) {
          fenetre.focus();
          fenetre.print();
        } 
      }
    } catch (error) {
      console.error("Erreur lors de l'appel POST /orders/{orderId}/print:", error);
    }
  };

  const hideZone = () => {
    const contentDiv = document.querySelector(".konvajs-content");
    if (contentDiv) {
      const secondCanvas = contentDiv.querySelectorAll("canvas")[1];
      if (secondCanvas) {
        const isHidden = secondCanvas.style.display === "none";
        secondCanvas.style.display = isHidden ? "block" : "none";
        setIsZoneVisible(isHidden);
      }
    }
  };

  const hideLens = () => {
    setIsLensVisible(!isLensVisible);
  };

  const isInteractiveMode = shapes && onHoveredShapeDraw && hoveredShapeId;

  const renderAnnotationViewer = () => {
    if (!imageLoaded || !annotationData) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p>{t("loading")}</p>
        </div>
      );
    }
    else if (imageLoaded && !orderImage?.source) {
      return (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <p>{t("unable_to_analyze_document")}</p>
        </div>
      );
    } else if (isInteractiveMode) {
      return (
        <AnnotationViewer
          style={{
            width: "100%",
            height: viewerHeight ? viewerHeight : "100%",
            background: "white",
            alignSelf: "flex-start",
            position: "absolute",
            top: atComputedTop,
          }}
          data={annotationData}
          getStage={(stage) => {
            annotationViewerStageRef.current = stage;
          }}
          customZoomLevel={customZoomLevel}
          onShapeMouseLeave={onShapeMouseLeave}
          onShapeMouseEnter={onShapeMouseEnter}
          getPointerPosition={updatePointerPosition}
        />
      );
    } else {
      return (
        <AnnotationViewer
          style={{
            width: "100%",
            height: viewerHeight ? viewerHeight : "100%",
            background: "white",
            alignSelf: "flex-start",
            position: "absolute",
            top: atComputedTop,
          }}
          data={annotationData}
          getStage={(stage) => {
            annotationViewerStageRef.current = stage;
          }}
          customZoomLevel={customZoomLevel}
          getPointerPosition={updatePointerPosition}
        />
      );
    }
  };

  return (
    <div ref={containerRef} className="w-full h-full relative">
      <div className="z-50 text-center px-4 bg-white sticky top-36">
        <div className="bg-vistadark/20 text-vistadark/50 rounded-lg py-1 flex justify-between text-xs">
          <button
            onClick={hideLens}
            className="bg-vistadark/30 text-white rounded-lg py-1 px-4 mx-2"
          >
            {isLensVisible ? t("hide_magnifier") : t("show_magnifier")}
          </button>
          <div className="bg-vistadark/30 text-white rounded-lg py-1 px-2 mx-2 flex items-center gap-2">
            <span>Zoom</span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => changeScale(0.8)}
                className="px-3 py-0.5 border border-white/30 rounded hover:bg-white/20 transition-colors"
              >
                −
              </button>
              <button
                onClick={() => changeScale(1.2)}
                className="px-3 py-0.5 border border-white/30 rounded hover:bg-white/20 transition-colors"
              >
                +
              </button>
            </div>
          </div>
          <button
            onClick={() => setCustomZoomLevel(1)}
            className="bg-vistadark/30 text-white rounded-lg py-1 px-4 mx-2"
          >
            {t("reset_zoom")}
          </button>
          <button
            onClick={hideZone}
            className="bg-vistadark/30 text-white rounded-lg py-1 px-4 mx-2"
          >
            {isZoneVisible ? t("hide_zones") : t("show_zones")}{" "}
            {/* Modification pour i18n */}
          </button>
          <button
            onClick={() => {
              const newValue = !isScrollSynced;
              if (newValue) {
                setLocalScrollOffset(0);
                setFrozenAtBottom(null);
              } else {
                setFrozenAtBottom(atBottom.bottom);
                setLocalScrollOffset(0);
              }
              setIsScrollSynced(newValue);
              onScrollSyncChange?.(newValue);
            }}
            className={`bg-vistadark/30 text-white rounded-lg py-1 px-4 mx-2 flex items-center ${isScrollSynced ? "" : "animate-pulse"}`}
          >
            {isScrollSynced ? <><FaLink className="mr-2" />{t("scroll_synced")}</> : <><FaUnlink className="mr-2" />{t("scroll_free")}</>}
          </button>
          {urlPdf && urlPdf.length > 0 && (
            <button
              onClick={imprimerPDF}
              className="bg-vistadark/30 text-white rounded-lg py-1 px-4 mx-2 flex items-center"
            >
              <MdPrint className="mr-2" /> {t("print")}
            </button>
          )}
        </div>
        <Lens
        pointerPosition={pointerPosition || { x: 0, y: 0 }}
        annotationData={annotationData}
        isDisplay={isLensVisible}
      />
      </div>

      {renderAnnotationViewer()}
      
    </div>
  );
}
